import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * PD-16919 — a report a user marked as a favorite, listed at the top of the
 * Reporting home page. Kept on the server, per tenant user, so favorites
 * follow the user across devices and the mobile app.
 *
 * `reportKey` identifies the report the way the reporting catalog does: a
 * fixed report by its path (`/reporting/reports/sales-analysis`) and a custom
 * report as `custom:<reportKey>` (see libs/shared utils/report-preference-key).
 * The server does not check that the report exists or that the user may open
 * it: a favorite is only a bookmark, the report's own endpoints enforce
 * access, and the home page hides any entry the user can no longer open.
 *
 * One row per (user, report). Unmarking soft-deletes the row and marking it
 * again recovers it, so the unique key is never violated.
 */
@Index('idx_report_favorite_user_report', ['fkUserId', 'reportKey'], {
	unique: true,
})
@Entity('report_favorite')
export class ReportFavoriteEntity extends BaseEntity {
	/** Tenant-local `user._id`, the id the session's instance user carries. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	@Column('varchar', { name: 'reportKey', length: 255 })
	public reportKey!: string;

	/** Position in the user's list, lowest first; a new favorite goes last. */
	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public user!: UserEntity;
}
